import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCustomers } from '../services/api';
import './CustomersPage.css';

// Citame customers are read-only: they are created automatically when a client
// writes via WhatsApp (keyed by phone). The admin can list, search and drill into
// a customer's appointment history — there is no manual create/edit/delete.
function CustomersPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const loadCustomers = useCallback(
    async (searchTerm) => {
      setLoading(true);
      try {
        const response = await getCustomers(searchTerm);
        setCustomers(response?.customers || []);
        setError('');
      } catch (err) {
        console.error('Error loading customers:', err);
        setCustomers([]);
        setError(t('customers.loadError'));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  // Debounced search: server-side matches by name (accent-insensitive) or phone.
  useEffect(() => {
    const timer = setTimeout(() => loadCustomers(search), 400);
    return () => clearTimeout(timer);
  }, [search, loadCustomers]);

  const formatLastAppointment = (value) => {
    if (!value) return '-';
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleDateString(i18n.language, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return value;
    }
  };

  return (
    <div className="customers-page">
      <div className="page-header">
        <h1>{t('customers.title')}</h1>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder={t('customers.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('customers.searchPlaceholder')}
        />
      </div>

      <p className="customers-data-legend">{t('customers.dataConsentLegend')}</p>

      {error && <div className="error-message">{error}</div>}

      <div className="card">
        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : customers.length === 0 ? (
          <div className="empty-state">
            {search ? t('customers.noResults') : t('customers.noCustomers')}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('customers.name')}</th>
                  <th>{t('customers.phone')}</th>
                  <th>{t('customers.appointmentsCount')}</th>
                  <th>{t('customers.lastAppointment')}</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr
                    key={customer.customerId}
                    className="customer-row"
                    onClick={() => navigate(`/customers/${encodeURIComponent(customer.customerId)}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/customers/${encodeURIComponent(customer.customerId)}`);
                      }
                    }}
                  >
                    <td>{customer.name || t('customers.unnamed')}</td>
                    <td>{customer.phone || customer.customerId || '-'}</td>
                    <td>
                      {customer.appointmentsCount != null ? customer.appointmentsCount : '-'}
                    </td>
                    <td>{formatLastAppointment(customer.lastAppointmentAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomersPage;
